import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout } from '../../services/authService';
import { getFeatures, AdminFeatures } from '../../services/adminApiService';
import { getAdminIdentity } from '../../services/providerReviewService';
import './Header.css';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Only show nav tabs when user is inside admin pages (not on /login)
  const showNav = location.pathname !== '/login';

  // Feature flags — fetched once when the user is on an authenticated page.
  // Starts as null (unknown) so we don't render the tab until the flag resolves,
  // avoiding a flicker where the tab briefly appears then disappears.
  const [features, setFeatures] = useState<AdminFeatures | null>(null);
  // Bot-review permission (Agent-Provider marketplace) — gates the Review tab.
  const [canReview, setCanReview] = useState(false);
  // The signed-in admin's email, as the API sees it. Review access is granted
  // per email in the allowlist, so this is the first thing to check when the
  // console shows less than expected.
  const [adminEmail, setAdminEmail] = useState('');

  useEffect(() => {
    if (!showNav) return; // not authenticated — skip the fetch
    let cancelled = false;
    getFeatures().then((f) => {
      if (!cancelled) setFeatures(f);
    });
    getAdminIdentity().then((id) => {
      if (cancelled) return;
      setCanReview(id.canReview);
      setAdminEmail(id.email);
    });
    return () => { cancelled = true; };
  }, [showNav]);

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to login page after successful logout
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to logout. Please try again.');
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ja' : 'en';
    i18n.changeLanguage(newLang);
  };

  return (
    <header className="admin-header">
      <div className="header-content">
        <div className="header-left">
          <h1 className="header-title">{t('app.title')}</h1>
          {showNav && (
            <nav className="header-nav">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  'nav-link' + (isActive ? ' nav-link--active' : '')
                }
              >
                {t('nav.pushTopic')}
              </NavLink>
              {/* Only render the Analytics tab once the flag has resolved and is enabled */}
              {features !== null && features.analytics && (
                <NavLink
                  to="/analytics"
                  className={({ isActive }) =>
                    'nav-link' + (isActive ? ' nav-link--active' : '')
                  }
                >
                  {t('nav.analytics')}
                </NavLink>
              )}
              {/* Review + All Bots tabs render only for admins with the reviewEnabled permission */}
              {canReview && (
                <NavLink
                  to="/review"
                  className={({ isActive }) =>
                    'nav-link' + (isActive ? ' nav-link--active' : '')
                  }
                >
                  {t('nav.review', 'Bot Review')}
                </NavLink>
              )}
              {canReview && (
                <NavLink
                  to="/all-bots"
                  className={({ isActive }) =>
                    'nav-link' + (isActive ? ' nav-link--active' : '')
                  }
                >
                  {t('nav.allBots', 'All Bots')}
                </NavLink>
              )}
              {canReview && (
                <NavLink
                  to="/marketing"
                  className={({ isActive }) =>
                    'nav-link' + (isActive ? ' nav-link--active' : '')
                  }
                >
                  {t('nav.marketing', 'Marketing')}
                </NavLink>
              )}
              {canReview && (
                <NavLink
                  to="/support"
                  className={({ isActive }) =>
                    'nav-link' + (isActive ? ' nav-link--active' : '')
                  }
                >
                  {t('nav.support', 'Support')}
                </NavLink>
              )}
            </nav>
          )}
        </div>
        <div className="header-right">
          {adminEmail && (
            <span className="admin-email" title={adminEmail}>{adminEmail}</span>
          )}
          <button className="language-toggle" onClick={toggleLanguage}>
            {i18n.language === 'en' ? '日本語' : 'English'}
          </button>
          <button className="logout-button" onClick={handleLogout}>
            {t('header.logout')}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
